import React, { useMemo } from 'react';
import { AlertIcon, Box, Checkbox, ChipsToggler, Field, FieldTypes, Inscription } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';
import * as yup from 'yup';

import { DEFAULT_DATE_FORMAT, REQUIRED_TEXT } from '@/common/constants';

import { OneTimeContributionFormValues } from '../contribution.types';

const currentYear = dayjs().year();
const currentToday = dayjs().toDate();
const maxAmount = 8750;

interface Props {
  formValues: OneTimeContributionFormValues;
  lastDayForContribution?: string;
  taxYear: string;
  setTaxYear: (value: string) => void;
  rollover?: boolean;
  setRollover?: (value: boolean) => void;
  minimumContributionAmount?: number;
  maximumContributionAmount?: number;
  isSubmitted?: boolean;
}

export const useMakeOneTimeContributionFields = (props: Props) => {
  const {
    lastDayForContribution,
    minimumContributionAmount = 10,
    maximumContributionAmount = maxAmount,
    rollover,
    setRollover,
    taxYear,
    setTaxYear,
    isSubmitted,
    formValues: { contributionAmount },
  } = props;
  const isPrevYearAvailable = useMemo(() => {
    if (!lastDayForContribution) return false;

    const lastDay = dayjs(lastDayForContribution).format(DEFAULT_DATE_FORMAT);
    const today = dayjs(currentToday).format(DEFAULT_DATE_FORMAT);

    return today <= lastDay;
  }, [lastDayForContribution]);

  const taxYearOptions = useMemo(
    () => [
      {
        key: `${currentYear}`,
        value: `${currentYear}`,
        title: `${currentYear}`,
      },
      ...(isPrevYearAvailable
        ? [
            {
              key: `${currentYear - 1}`,
              value: `${currentYear - 1}`,
              title: `${currentYear - 1}`,
            },
          ]
        : []),
    ],
    [isPrevYearAvailable]
  );

  const fields = useMemo(
    () =>
      [
        {
          name: 'contributionAmount',
          type: FieldTypes.Currency,
          showRequireIcon: true,
          label: 'Contribution amount',
          placeholder: 'Enter Contribution amount',
          defaultValue: contributionAmount,
          validator: yup
            .string()
            .test({
              test: (val) => {
                if (_.toNumber(val) < minimumContributionAmount) return false;
                return true;
              },
              message: `Amount must be greater than $${minimumContributionAmount}`,
            })
            .test({
              test: (val) => {
                if (_.toNumber(val) > maximumContributionAmount && !rollover) return false;
                return true;
              },
              message: 'Amount exceeds your remaining limit',
            })
            .required(REQUIRED_TEXT),
        },
        {
          name: 'taxYear',
          type: FieldTypes.Node,
          showRequireIcon: true,
          label: 'Tax year',
          value: (
            <Box>
              <ChipsToggler
                value={taxYear}
                options={taxYearOptions}
                onChange={setTaxYear}
                testId="OneTimeContributionForm_TaxYear"
              />
              {!taxYear && isSubmitted && (
                <Box margin={{ top: 'spacing4' }}>
                  <Box direction="row" align="center">
                    <AlertIcon size="12px" />
                    <Inscription size="12px" color="danger" margin={{ left: 'spacing8' }} lineHeight="20px">
                      {REQUIRED_TEXT}
                    </Inscription>
                  </Box>
                </Box>
              )}
            </Box>
          ),
          // type: FieldTypes.Dropdown,
          // showRequireIcon: true,
          // label: 'Tax year',
          // placeholder: 'Enter Tax year',
          // validator: yup.string().required(REQUIRED_TEXT),
          // singleMode: true,
          // options: taxYearOptions,
          // defaultValue: taxYear || `${currentYear}`,
        },
        {
          name: 'rollover',
          type: FieldTypes.Node,
          label: (
            <Box width={{ max: '245px' }}>
              <Inscription margin={{ bottom: '4px' }} weight="bold" color="textBody">
                Rollover
              </Inscription>
              <Box>
                <Inscription weight={500} color="textSecondary" size="small">
                  This contribution is a rollover of money from another HSA.
                </Inscription>
              </Box>
            </Box>
          ),
          value: (
            <Box>
              <Checkbox checked={rollover} onChange={setRollover} />
            </Box>
          ),
        },
      ] as Field[],
    [
      contributionAmount,
      rollover,
      setRollover,
      taxYear,
      taxYearOptions,
      minimumContributionAmount,
      maximumContributionAmount,
      setTaxYear,
      isSubmitted,
    ]
  );

  return fields;
};
